import React, { useEffect, useRef } from "react";
import { BookingStep, TimeSlot, BookingLanguage } from "@/types/booking";
import DateStep from "@/components/DateStep";
import TimeSlotGrid from "@/components/TimeSlotGrid";
import BookingForm from "@/components/BookingForm";
import PriceSummary from "@/components/booking/price/PriceSummary";
import { Product } from "@/components/product-step/types";
import { FormValues } from "@/components/booking-form/types";
import { scrollToTopDelayed } from "@/utils/scrollUtils";
import { ExpandableOrderSummary } from "@/components/booking/ExpandableOrderSummary";
import { useClinics } from "@/hooks/useClinics";
import { useIsMobile } from "@/hooks/use-mobile";
import { useBookingFlow } from "@/hooks/useBookingFlow";
import type { SlotSelectionUpdateOptions } from "@/hooks/booking/useTimeSlots";
import { useBookingStore } from "@/stores/bookingStore";
import { useMembership } from "@/contexts/MembershipContext";
import { t as translate } from "@/locales";

interface ContentLayoutProps {
  currentStep: BookingStep;
  currentDate?: Date;
  handleDateSelect: (date: Date | undefined) => void;
  selectedTimeSlots: TimeSlot[];
  language: BookingLanguage;
  selectedClinic: string;
  handleTimeSelect: (
    times: string[],
    options?: SlotSelectionUpdateOptions,
  ) => void;
  handleDeleteTimeSlot: (slot: TimeSlot) => void;
  setCurrentStep: (step: BookingStep) => void;
  bookingType: "request";
  setBookingType: (type: "request") => void;
  handleFormSubmit: (data: FormValues) => void;
  selectedProductDetails: Product[];
  discount: {
    code: string;
    type: "percentage" | "fixed_amount";
    value: number;
  } | null;
  bookingId: string;
  selectedProducts: string[];
  selectedAddOns?: string[]; // Add selectedAddOns prop
  t: { selectedProducts: string; totalPrice: string };
  packageComponentMap?: Record<string, string[]>;
  handleBack: () => void;
  canGoBack: boolean;
  // Add removal handlers
  handleProductRemove?: (productId: string) => void;
  onAddOnToggle?: (addOnId: string) => void;
}

const ContentLayout: React.FC<ContentLayoutProps> = ({
  currentStep,
  currentDate,
  handleDateSelect,
  selectedTimeSlots,
  language,
  selectedClinic,
  handleTimeSelect,
  handleDeleteTimeSlot,
  setCurrentStep,
  bookingType,
  setBookingType,
  handleFormSubmit,
  selectedProductDetails,
  discount,
  bookingId,
  selectedProducts,
  selectedAddOns = [], // Add selectedAddOns with default empty array
  t,
  packageComponentMap,
  handleBack,
  canGoBack,
  handleProductRemove,
  onAddOnToggle,
}) => {
  const { data: clinics } = useClinics();
  const isMobile = useIsMobile();
  const { isSubmitting, isSubmitted } = useBookingFlow();
  const { getEffectivePrice } = useMembership();
  const clearInvitationCode = useBookingStore(
    (state) => state.clearInvitationCode,
  );
  const previousStepRef = useRef<BookingStep>(currentStep);

  // Clear invitation code when navigating away from details step
  useEffect(() => {
    if (previousStepRef.current === "details" && currentStep !== "details") {
      console.log(
        "ContentLayout - Clearing invitation code (navigated away from details)",
      );
      clearInvitationCode();
    }
    previousStepRef.current = currentStep;
  }, [currentStep, clearInvitationCode]);

  // Clear invitation code when ContentLayout unmounts while on details step
  // (e.g., navigating from details to clinic/product/addons)
  useEffect(() => {
    return () => {
      if (previousStepRef.current === "details") {
        console.log(
          "ContentLayout - Clearing invitation code (unmounting from details)",
        );
        clearInvitationCode();
      }
    };
  }, [clearInvitationCode]);

  // Removed handleInternalBack - use store's handleBack directly which properly manages step history
  // The store's goBack() handles all navigation logic including blood-only package special cases

  const handleEditProducts = () => {
    setCurrentStep("product");
    // Scroll to top on navigation to product step
    scrollToTopDelayed(50);
  };

  if (currentStep === "date") {
    // Separate main products from add-ons
    const mainProducts = selectedProductDetails.filter((product) =>
      selectedProducts.includes(product.id),
    );
    const addOnProducts = selectedProductDetails.filter((product) =>
      selectedAddOns.includes(product.id),
    );

    // Calculate total price (membership-aware)
    const basePrice = selectedProductDetails.reduce(
      (sum, product) => sum + getEffectivePrice(product),
      0,
    );

    let totalPrice = basePrice;
    if (discount) {
      if (discount.type === "percentage") {
        totalPrice = basePrice - (basePrice * discount.value) / 100;
      } else {
        totalPrice = Math.max(0, basePrice - discount.value);
      }
    }

    return <DateStep />;
  }

  if (currentStep === "time") {
    // Separate main products from add-ons
    const mainProducts = selectedProductDetails.filter((product) =>
      selectedProducts.includes(product.id),
    );
    const addOnProducts = selectedProductDetails.filter((product) =>
      selectedAddOns.includes(product.id),
    );

    // Calculate total price (membership-aware)
    const basePrice = selectedProductDetails.reduce(
      (sum, product) => sum + getEffectivePrice(product),
      0,
    );

    let totalPrice = basePrice;
    if (discount) {
      if (discount.type === "percentage") {
        totalPrice = basePrice - (basePrice * discount.value) / 100;
      } else {
        totalPrice = Math.max(0, basePrice - discount.value);
      }
    }

    return (
      <TimeSlotGrid
        date={currentDate!}
        onTimeSelect={handleTimeSelect}
        bookingType={bookingType}
        setBookingType={setBookingType}
        language={language}
        selectedTimeSlots={selectedTimeSlots}
        onDeleteTimeSlot={handleDeleteTimeSlot}
        setCurrentStep={setCurrentStep}
        selectedClinic={selectedClinic}
        packageComponentMap={packageComponentMap}
        onBack={handleBack}
        canGoBack={canGoBack}
        // Pass order summary data
        selectedProducts={selectedProducts}
        selectedAddOns={selectedAddOns}
        selectedProductDetails={mainProducts}
        selectedAddOnDetails={addOnProducts}
        totalPrice={totalPrice}
        // Pass removal handlers
        onProductRemove={handleProductRemove}
        onAddOnRemove={onAddOnToggle}
      />
    );
  }

  if (currentStep === "details") {
    // Separate main products from add-ons
    const mainProducts = selectedProductDetails.filter((product) =>
      selectedProducts.includes(product.id),
    );
    const addOnProducts = selectedProductDetails.filter((product) =>
      selectedAddOns.includes(product.id),
    );

    // Calculate total price (membership-aware)
    const basePrice = selectedProductDetails.reduce(
      (sum, product) => sum + getEffectivePrice(product),
      0,
    );

    let totalPrice = basePrice;
    if (discount) {
      if (discount.type === "percentage") {
        totalPrice = basePrice - (basePrice * discount.value) / 100;
      } else {
        totalPrice = Math.max(0, basePrice - discount.value);
      }
    }

    // Get selected clinic object
    const selectedClinicObject = clinics?.find((c) => c.id === selectedClinic);

    const orderSummaryTranslations = {
      orderSummary: translate(language, "orderSummary.title"),
      scan: translate(language, "orderSummary.scan"),
      addOns: translate(language, "orderSummary.addOns"),
      bloodTests: translate(language, "orderSummary.bloodTests"),
      location: translate(language, "orderSummary.location"),
      timeSlots: translate(language, "orderSummary.timeSlots"),
      totalPrice: translate(language, "orderSummary.totalPrice"),
      continue: translate(language, "common.next"),
      back: translate(language, "common.prev"),
      remove: translate(language, "common.remove"),
    };

    return (
      <>
        <div className="space-y-6 pb-12">
          {isMobile && (
            <PriceSummary
              selectedProductDetails={selectedProductDetails}
              discount={discount}
              t={t}
              language={language}
              packageComponentMap={packageComponentMap}
              onEditProducts={handleEditProducts}
            />
          )}
          <BookingForm
            discount={discount}
            packageComponentMap={packageComponentMap}
            onBack={handleBack}
          />
        </div>

        {/* Expandable Order Summary Footer */}
        <ExpandableOrderSummary
          totalPrice={totalPrice}
          basePrice={basePrice}
          selectedProducts={mainProducts}
          selectedAddOns={addOnProducts}
          selectedClinic={selectedClinicObject}
          selectedTimeSlots={selectedTimeSlots}
          language={language}
          showBackButton={true}
          showContinueButton={false} // Hide continue button since form has its own submit
          onBack={handleBack}
          onAddOnRemove={onAddOnToggle}
          onEditProducts={handleEditProducts}
          onEditAddOns={
            setCurrentStep ? () => setCurrentStep("addons") : undefined
          }
          onEditLocation={
            setCurrentStep ? () => setCurrentStep("clinic") : undefined
          }
          onEditTimeSlots={
            setCurrentStep ? () => setCurrentStep("date") : undefined
          }
          translations={orderSummaryTranslations}
          isSubmitting={isSubmitting || isSubmitted}
          isLoading={isSubmitting || isSubmitted}
          currentStep={currentStep}
        />
      </>
    );
  }

  return null;
};

export default ContentLayout;
